import { useEffect, useMemo, useState } from 'react';
import { Bell, BellOff, MessageCircle, Pencil, Plus, Radio, Star, Trash2, Users } from 'lucide-react';
import { AddTargetModal } from '@/components/modals/AddTargetModal';
import { Button } from '@/components/ui/Button';
import { usePersonasStore } from '@/stores/personasStore';
import { useTargetsStore, type AddTargetInput, type Target, type TargetType } from '@/stores/targetsStore';

const categoryLabels: Record<TargetType | 'all', string> = {
  all: 'All people',
  followed: 'Followed',
  inspirator: 'Inspirators',
  impress: 'Impress targets',
};

const categoryStyles: Record<TargetType, string> = {
  followed: 'bg-blue-500/10 text-blue-700 border-blue-500/40 dark:text-blue-200',
  inspirator: 'bg-amber-500/15 text-amber-700 border-amber-500/50 dark:text-amber-200',
  impress: 'bg-emerald-500/15 text-emerald-700 border-emerald-500/50 dark:text-emerald-200',
};

function getTargetDisplayName(target: Target): string {
  return target.nickname?.trim() || target.full_name || 'LinkedIn Profile';
}

function getPersonaName(personaId: string | null | undefined, personas: Array<{ id: string; name: string }>): string {
  return personas.find((persona) => persona.id === personaId)?.name ?? 'Shared network';
}

export function TargetsPage() {
  const { targets, isLoading, error, fetchTargets, addTarget, removeTarget, updateTarget } = useTargetsStore();
  const { personas, selectedPersona, fetchPersonas, setSelectedPersona } = usePersonasStore();

  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [personaFilter, setPersonaFilter] = useState<string>('all');
  const [categoryFilter, setCategoryFilter] = useState<TargetType | 'all'>('all');

  useEffect(() => {
    void fetchTargets();
    void fetchPersonas();
  }, [fetchPersonas, fetchTargets]);

  const filteredTargets = useMemo(() => {
    return targets.filter((target) => {
      const personaMatches = personaFilter === 'all' || (personaFilter === 'unassigned' ? !target.persona_id : target.persona_id === personaFilter);
      const categoryMatches = categoryFilter === 'all' || (target.type ?? 'followed') === categoryFilter;
      return personaMatches && categoryMatches;
    });
  }, [categoryFilter, personaFilter, targets]);

  const countsByType = useMemo(() => {
    return targets.reduce(
      (acc, target) => {
        acc[target.type ?? 'followed'] += 1;
        return acc;
      },
      { followed: 0, inspirator: 0, impress: 0 },
    );
  }, [targets]);

  const handleAddTarget = async (input: AddTargetInput) => {
    await addTarget(input);
    setIsAddModalOpen(false);
  };

  const handleDeleteTarget = async (targetId: string) => {
    await removeTarget(targetId);
  };

  const handleToggleFavorite = async (target: Target) => {
    await updateTarget(target.id, { is_favorite: !target.is_favorite });
  };

  const handleTogglePostAlert = async (target: Target) => {
    await updateTarget(target.id, { notify_on_post: !target.notify_on_post });
  };

  const handleToggleCommentAlert = async (target: Target) => {
    await updateTarget(target.id, { notify_on_comment: !target.notify_on_comment });
  };

  const handleEditTarget = async (target: Target) => {
    const nickname = window.prompt('Nickname', target.nickname ?? '') ?? target.nickname ?? '';
    const notes = window.prompt('Notes', target.notes ?? '') ?? target.notes ?? '';
    await updateTarget(target.id, { nickname, notes });
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      <section className="brutalist-panel relative overflow-hidden rounded-3xl p-6 lg:p-8">
        <div className="absolute right-6 top-6 hidden rounded-full border-2 border-border px-3 py-1 text-xs font-black uppercase tracking-[0.2em] text-muted-foreground lg:block">
          {targets.length} network people
        </div>
        <div className="max-w-4xl">
          <p className="brutalist-label">Network Intelligence</p>
          <h1 className="mt-3 text-4xl font-black leading-none tracking-[-0.08em] md:text-6xl">
            Map the people around each managed persona.
          </h1>
          <p className="mt-4 max-w-2xl text-base font-medium text-muted-foreground md:text-lg">
            Track followed people, inspirators and impress targets with persona assignment, alert cadence and relationship context.
          </p>
        </div>
        <div className="mt-6 flex flex-wrap gap-3">
          <Button
            className="rounded-xl border-2 border-border bg-primary px-5 py-3 text-sm font-black text-primary-foreground shadow-[4px_4px_0_hsl(var(--foreground)/0.22)] transition-transform hover:-translate-y-0.5 dark:shadow-[4px_4px_0_hsl(var(--primary)/0.24)]"
            onClick={() => setIsAddModalOpen(true)}
            disabled={isLoading}
          >
            <Plus className="mr-2 h-4 w-4" />
            Add person
          </Button>
          <div className="flex items-center rounded-xl border-2 border-border bg-card px-4 py-2 text-sm font-black text-muted-foreground">
            {filteredTargets.length} of {targets.length} people shown
          </div>
        </div>
      </section>

      <section className="grid gap-3 md:grid-cols-3">
        {(['followed', 'inspirator', 'impress'] as TargetType[]).map((type) => (
          <button
            key={type}
            type="button"
            className={`brutalist-panel-soft rounded-3xl p-4 text-left transition-transform hover:-translate-y-1 ${categoryFilter === type ? 'border-primary bg-primary/5' : ''}`}
            onClick={() => setCategoryFilter(categoryFilter === type ? 'all' : type)}
          >
            <p className="text-xs font-black uppercase tracking-[0.18em] text-muted-foreground">{categoryLabels[type]}</p>
            <p className="mt-2 text-4xl font-black tracking-[-0.08em]">{countsByType[type]}</p>
          </button>
        ))}
      </section>

      <section className="brutalist-panel-soft rounded-3xl p-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center">
          <div className="grid gap-3 md:grid-cols-2">
            <label className="space-y-1">
              <span className="brutalist-label">Managed persona</span>
              <select
                className="h-10 w-full rounded-xl border-2 border-border bg-background px-3 py-1 text-sm font-bold shadow-sm md:w-64"
                value={personaFilter}
                onChange={(event) => {
                  const value = event.target.value;
                  setPersonaFilter(value);
                  setSelectedPersona(personas.find((persona) => persona.id === value) ?? null);
                }}
              >
                <option value="all">All personas</option>
                <option value="unassigned">Shared / unassigned</option>
                {personas.map((persona) => (
                  <option key={persona.id} value={persona.id}>{persona.name}</option>
                ))}
              </select>
            </label>
            <label className="space-y-1">
              <span className="brutalist-label">Network category</span>
              <select
                className="h-10 w-full rounded-xl border-2 border-border bg-background px-3 py-1 text-sm font-bold shadow-sm md:w-64"
                value={categoryFilter}
                onChange={(event) => setCategoryFilter(event.target.value as TargetType | 'all')}
              >
                <option value="all">All categories</option>
                <option value="followed">Followed</option>
                <option value="inspirator">Inspirators</option>
                <option value="impress">Impress targets</option>
              </select>
            </label>
          </div>
          <p className="rounded-xl border border-border bg-card px-3 py-2 text-sm font-black text-muted-foreground md:ml-auto">
            {filteredTargets.length} of {targets.length} people shown
          </p>
        </div>
      </section>

      {error ? (
        <div className="brutalist-panel-soft rounded-2xl border-destructive/70 bg-destructive/5 p-4 text-sm font-bold text-destructive">
          {error}
        </div>
      ) : null}

      {isLoading && targets.length === 0 ? (
        <div className="grid gap-4 xl:grid-cols-2">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={`target-skeleton-${index}`} className="brutalist-panel-soft rounded-3xl p-5">
              <div className="flex items-start gap-4">
                <div className="h-14 w-14 animate-pulse rounded-xl border-2 border-border bg-muted" />
                <div className="min-w-0 flex-1 space-y-3">
                  <div className="h-5 w-2/3 animate-pulse rounded bg-muted" />
                  <div className="h-4 w-full animate-pulse rounded bg-muted" />
                  <div className="h-4 w-4/5 animate-pulse rounded bg-muted" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : filteredTargets.length === 0 ? (
        <div className="brutalist-panel-soft rounded-3xl border-dashed p-10 text-center">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl border-2 border-border bg-secondary">
            <Users className="h-7 w-7 text-muted-foreground" />
          </div>
          <p className="mt-4 text-xl font-black tracking-[-0.06em]">No people in this network view</p>
          <p className="mx-auto mt-2 max-w-md text-sm font-medium text-muted-foreground">
            Add a LinkedIn profile and assign it to a managed persona/category to start monitoring.
          </p>
        </div>
      ) : (
        <section className="grid gap-4 xl:grid-cols-2">
          {filteredTargets.map((target) => {
            const displayName = getTargetDisplayName(target);
            const type = target.type ?? 'followed';
            const initial = displayName.charAt(0).toUpperCase();
            return (
              <article key={target.id} className={`brutalist-panel-soft flex min-h-full flex-col rounded-3xl p-5 transition-transform hover:-translate-y-1 ${target.is_active === false ? 'opacity-70' : ''}`}>
                <div className="flex items-start gap-4">
                  {target.profile_picture_url ? (
                    <img src={target.profile_picture_url} alt={displayName} className="h-14 w-14 rounded-xl border-2 border-border object-cover" />
                  ) : (
                    <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border-2 border-border bg-primary text-lg font-black text-primary-foreground">
                      {initial}
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-xl font-black tracking-[-0.06em]">{displayName}</h2>
                      <span className={`rounded-full border px-2 py-1 text-[11px] font-black uppercase tracking-wide ${categoryStyles[type]}`}>{categoryLabels[type]}</span>
                      {target.priority ? <span className="rounded-full border border-border bg-secondary px-2 py-1 text-[11px] font-black uppercase tracking-wide">P{target.priority}</span> : null}
                    </div>
                    <p className="mt-2 line-clamp-2 text-sm font-medium text-muted-foreground">{target.headline ?? target.notes ?? 'No headline synced yet.'}</p>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs font-black">
                      <span className="rounded-full border border-border bg-card px-2 py-1">Persona: {getPersonaName(target.persona_id, personas)}</span>
                      <span className="rounded-full border border-border bg-card px-2 py-1">Alerts: {target.alert_frequency ?? 'daily'}</span>
                    </div>
                    {target.relationship_tags?.length ? (
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {target.relationship_tags.map((tag) => <span key={tag} className="rounded-full border border-border bg-background/45 px-2 py-1 text-xs font-bold">{tag}</span>)}
                      </div>
                    ) : null}
                  </div>
                </div>

                <div className="mt-auto flex flex-wrap items-center gap-2 border-t-2 border-border pt-4">
                  <Button variant="outline" size="sm" className="rounded-xl border-2 border-border font-black" onClick={() => void handleTogglePostAlert(target)} disabled={isLoading}>
                    {target.notify_on_post ? <Bell className="mr-1 h-4 w-4" /> : <BellOff className="mr-1 h-4 w-4" />}
                    Posts
                  </Button>
                  <Button variant="outline" size="sm" className="rounded-xl border-2 border-border font-black" onClick={() => void handleToggleCommentAlert(target)} disabled={isLoading}>
                    <MessageCircle className="mr-1 h-4 w-4" />
                    Comments {target.notify_on_comment ? 'on' : 'off'}
                  </Button>
                  <Button variant="ghost" size="sm" className="rounded-xl font-black" onClick={() => void handleToggleFavorite(target)} disabled={isLoading}>
                    <Star className={target.is_favorite ? 'mr-1 h-4 w-4 fill-yellow-400 text-yellow-400' : 'mr-1 h-4 w-4'} />
                    Favorite
                  </Button>
                  <Button variant="ghost" size="sm" className="rounded-xl font-black" onClick={() => void updateTarget(target.id, { is_active: !(target.is_active ?? true) })} disabled={isLoading}>
                    <Radio className="mr-1 h-4 w-4" />
                    {(target.is_active ?? true) ? 'Active' : 'Paused'}
                  </Button>
                  <div className="ml-auto flex gap-2">
                    <Button variant="outline" size="sm" className="rounded-xl border-2 border-border font-black" onClick={() => void handleEditTarget(target)} disabled={isLoading}><Pencil className="mr-1 h-4 w-4" />Edit</Button>
                    <Button variant="ghost" size="sm" className="rounded-xl font-black text-destructive hover:bg-destructive/10" onClick={() => void handleDeleteTarget(target.id)} disabled={isLoading}><Trash2 className="mr-1 h-4 w-4" />Delete</Button>
                  </div>
                </div>
              </article>
            );
          })}
        </section>
      )}

      <AddTargetModal
        isOpen={isAddModalOpen}
        isLoading={isLoading}
        personas={personas}
        defaultPersonaId={selectedPersona?.id}
        onClose={() => setIsAddModalOpen(false)}
        onSave={handleAddTarget}
      />
    </div>
  );
}
